"use client";

import Image from "next/image";
import { Link } from "@/i18n/navigation";
import {
  PhoneIcon,
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  MapPinIcon,
  ClockIcon,
  ShieldCheckIcon,
  HomeModernIcon,
  ArchiveBoxIcon,
  BuildingOffice2Icon,
  GiftIcon,
  ArrowRightIcon,
  StarIcon,
  TruckIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import CtaLink from "@/components/ui/CtaLink";
import Contact from "@/components/Contact/Contact";
import { bruxellesData } from "@/lib/communesBruxelles";

const PHONE_TEL = "+32493966507";
const PHONE_DISPLAY = "+32 (0)493 96 65 07";

const FEATURED_COMMUNES = [
  "bruxelles-ville", "ixelles", "uccle", "schaerbeek", "anderlecht",
  "saint-gilles", "etterbeek", "woluwe-saint-pierre",
];

const FEATURE_ICONS = [TruckIcon, ClipboardDocumentCheckIcon, GiftIcon, GlobeAltIcon];
const SERVICE_ICONS = [HomeModernIcon, BuildingOffice2Icon, ArchiveBoxIcon, GiftIcon];
const REASSURANCE_ICONS = [ShieldCheckIcon, ClockIcon, StarIcon];

const CONTENT = {
  fr: {
    heroBadge: "Les 19 communes de Bruxelles",
    h1: {
      before: "Vide maison à ",
      accent: "Bruxelles",
      after: " — débarras complet et rachat d'objets de valeur",
    },
    heroText:
      "Nous débarrassons maisons, appartements, caves, greniers et garages dans toute la Région bruxelloise — succession, vente, fin de bail ou simple envie de faire place nette. Devis gratuit, intervention rapide, et rachat possible de vos objets de valeur.",
    freeQuote: "Devis gratuit",
    heroImageAlt:
      "Camion Vide Maison Bruxelles, équipe chargeant un meuble, Atomium et hôtel de ville en arrière-plan",
    heroCaptionBefore: "Service rapide, propre et ",
    heroCaptionAccent: "100% fiable",
    heroCaptionMobileBefore: "Rapide · Propre · ",
    heroCaptionMobileAccent: "100% fiable",
    features: [
      { title: "Intervention rapide", text: "Équipe locale disponible 7j/7 dans Bruxelles" },
      { title: "Devis gratuit", text: "Estimation claire et sans engagement" },
      { title: "Rachat d'objets", text: "Nous achetons vos objets de valeur" },
      { title: "Évacuation responsable", text: "Tri, recyclage et respect de l'environnement" },
    ],
    rachat: {
      badge: "Rachat d'objets de valeur",
      title: "Vos objets de valeur peuvent réduire le coût de votre débarras",
      text: "Avant toute évacuation, nous examinons ce qui a une réelle valeur : meubles anciens, bijoux, argenterie, tableaux, collections. Lorsque c'est le cas, nous les rachetons ou déduisons leur valeur du prix global du débarras — une différence concrète pour votre budget.",
      items: [
        "Mobilier ancien et de style",
        "Bijoux et montres",
        "Argenterie et vaisselle",
        "Tableaux et objets d'art",
        "Livres, vinyles et collections",
        "Horloges et objets de famille",
      ],
      cta: "En savoir plus sur le rachat",
      howItWorksTitle: "Comment ça fonctionne ?",
      steps: [
        { n: "1", title: "Visite ou photos", text: "Nous évaluons le bien sur place ou via vos photos." },
        { n: "2", title: "Estimation transparente", text: "Les objets valorisables sont identifiés et estimés." },
        { n: "3", title: "Devis déduit", text: "Leur valeur est déduite directement du prix du débarras." },
      ],
    },
    services: {
      title: "Nos services de vide maison",
      text: "Un service complet, adapté à chaque situation, dans toute la Région bruxelloise.",
      cards: [
        { title: "Vide maison", text: "Débarras complet d'une maison, du grenier à la cave.", link: "/vide-maison" },
        { title: "Débarras appartement", text: "Studios, appartements et immeubles, à tout étage.", link: "/debarras-appartement" },
        { title: "Cave, grenier, garage", text: "Espaces annexes encombrés depuis des années.", link: "/debarras-cave-grenier-garage" },
        { title: "Rachat d'objets", text: "Estimation et rachat des objets de valeur trouvés.", link: "/rachat-objets" },
      ],
      learnMore: "En savoir plus",
    },
    process: {
      title: "Comment se déroule l'intervention",
      text: "Un processus simple, du premier contact à l'évacuation complète.",
      steps: [
        { n: "1", title: "Contact", text: "Vous nous appelez ou remplissez le formulaire." },
        { n: "2", title: "Devis gratuit", text: "Estimation rapide, sur place ou par photos." },
        { n: "3", title: "Intervention", text: "Débarras complet à la date convenue." },
        { n: "4", title: "Bien libéré", text: "Le bien est propre et prêt à être remis." },
      ],
    },
    communes: {
      title: "Vide maison dans les 19 communes de Bruxelles",
      text: "Nous intervenons dans toute la Région de Bruxelles-Capitale, avec une bonne connaissance de chaque quartier.",
      seeAll: "Voir les 19 communes desservies",
    },
    reassurance: [
      { title: "Service sérieux", text: "Une équipe expérimentée, discrète et respectueuse de votre logement." },
      { title: "Intervention rapide", text: "Des créneaux disponibles rapidement, y compris en cas d'urgence." },
      { title: "Clients satisfaits", text: "Des interventions menées avec soin, saluées par nos clients." },
    ],
  },
  nl: {
    heroBadge: "De 19 gemeenten van Brussel",
    h1: {
      before: "Huis leegmaken in ",
      accent: "Brussel",
      after: " — volledige ontruiming en inkoop van waardevolle voorwerpen",
    },
    heroText:
      "Wij ontruimen huizen, appartementen, kelders, zolders en garages in het hele Brussels Gewest — bij een erfenis, verkoop, einde huurcontract of gewoon om plaats te maken. Gratis offerte, snelle interventie en mogelijke inkoop van uw waardevolle voorwerpen.",
    freeQuote: "Gratis offerte",
    heroImageAlt:
      "Vrachtwagen van Vide Maison Bruxelles, team dat meubels laadt, Atomium en stadhuis op de achtergrond",
    heroCaptionBefore: "Snelle, nette en ",
    heroCaptionAccent: "100% betrouwbare dienst",
    heroCaptionMobileBefore: "Snel · Net · ",
    heroCaptionMobileAccent: "100% betrouwbaar",
    features: [
      { title: "Snelle interventie", text: "Lokaal team 7 dagen op 7 beschikbaar in Brussel" },
      { title: "Gratis offerte", text: "Duidelijke, vrijblijvende schatting" },
      { title: "Inkoop van voorwerpen", text: "Wij kopen uw waardevolle voorwerpen" },
      { title: "Verantwoorde afvoer", text: "Sortering, recyclage en respect voor het milieu" },
    ],
    rachat: {
      badge: "Inkoop van waardevolle voorwerpen",
      title: "Uw waardevolle voorwerpen kunnen de kostprijs van uw ontruiming verlagen",
      text: "Voor elke afvoer bekijken we wat werkelijk waarde heeft: antiek meubilair, juwelen, zilverwerk, schilderijen, verzamelingen. Als dat het geval is, kopen we ze aan of trekken we hun waarde af van de totale prijs van de ontruiming — een concreet verschil voor uw budget.",
      items: [
        "Antiek en stijlmeubilair",
        "Juwelen en horloges",
        "Zilverwerk en servies",
        "Schilderijen en kunstvoorwerpen",
        "Boeken, vinylplaten en verzamelingen",
        "Klokken en familiestukken",
      ],
      cta: "Meer over de inkoop van voorwerpen",
      howItWorksTitle: "Hoe werkt het?",
      steps: [
        { n: "1", title: "Bezoek of foto's", text: "Wij evalueren het pand ter plaatse of aan de hand van uw foto's." },
        { n: "2", title: "Transparante schatting", text: "De waardevolle voorwerpen worden geïdentificeerd en geschat." },
        { n: "3", title: "Verrekende offerte", text: "Hun waarde wordt rechtstreeks afgetrokken van de ontruimingsprijs." },
      ],
    },
    services: {
      title: "Onze diensten voor het leegmaken van huizen",
      text: "Een volledige dienst, aangepast aan elke situatie, in het hele Brussels Gewest.",
      cards: [
        { title: "Huis leegmaken", text: "Volledige ontruiming van een huis, van zolder tot kelder.", link: "/vide-maison" },
        { title: "Appartement ontruimen", text: "Studio's, appartementen en gebouwen, op elke verdieping.", link: "/debarras-appartement" },
        { title: "Kelder, zolder, garage", text: "Bijruimtes die al jaren vol staan.", link: "/debarras-cave-grenier-garage" },
        { title: "Inkoop van voorwerpen", text: "Schatting en inkoop van gevonden waardevolle voorwerpen.", link: "/rachat-objets" },
      ],
      learnMore: "Meer weten",
    },
    process: {
      title: "Hoe verloopt de interventie",
      text: "Een eenvoudig proces, van het eerste contact tot de volledige afvoer.",
      steps: [
        { n: "1", title: "Contact", text: "U belt ons of vult het formulier in." },
        { n: "2", title: "Gratis offerte", text: "Snelle schatting, ter plaatse of via foto's." },
        { n: "3", title: "Interventie", text: "Volledige ontruiming op de afgesproken datum." },
        { n: "4", title: "Pand vrijgemaakt", text: "Het pand is proper en klaar om overgedragen te worden." },
      ],
    },
    communes: {
      title: "Huis leegmaken in de 19 gemeenten van Brussel",
      text: "Wij zijn actief in het hele Brussels Hoofdstedelijk Gewest, met een goede kennis van elke buurt.",
      seeAll: "Bekijk de 19 bediende gemeenten",
    },
    reassurance: [
      { title: "Serieuze dienstverlening", text: "Een ervaren team, discreet en respectvol voor uw woning." },
      { title: "Snelle interventie", text: "Snel beschikbare tijdslots, ook in dringende gevallen." },
      { title: "Tevreden klanten", text: "Interventies met zorg uitgevoerd, geprezen door onze klanten." },
    ],
  },
  en: {
    heroBadge: "All 19 communes of Brussels",
    h1: {
      before: "House clearance in ",
      accent: "Brussels",
      after: " — full clearance and buyback of valuable items",
    },
    heroText:
      "We clear houses, apartments, cellars, attics and garages across the entire Brussels Region — for an estate, a sale, the end of a lease, or simply to make a fresh start. Free quote, fast intervention, and possible buyback of your valuable items.",
    freeQuote: "Free quote",
    heroImageAlt:
      "Vide Maison Bruxelles truck, team loading furniture, Atomium and city hall in the background",
    heroCaptionBefore: "Fast, tidy and ",
    heroCaptionAccent: "100% reliable service",
    heroCaptionMobileBefore: "Fast · Clean · ",
    heroCaptionMobileAccent: "100% reliable",
    features: [
      { title: "Fast intervention", text: "Local team available 7 days a week in Brussels" },
      { title: "Free quote", text: "Clear, no-obligation estimate" },
      { title: "Item buyback", text: "We buy your valuable items" },
      { title: "Responsible disposal", text: "Sorting, recycling and respect for the environment" },
    ],
    rachat: {
      badge: "Buyback of valuable items",
      title: "Your valuable items can lower the cost of your clearance",
      text: "Before any removal, we look at what has real value: antique furniture, jewellery, silverware, paintings, collections. When that's the case, we buy them back or deduct their value from the total clearance price — a real difference for your budget.",
      items: [
        "Antique and period furniture",
        "Jewellery and watches",
        "Silverware and tableware",
        "Paintings and art objects",
        "Books, vinyl records and collections",
        "Clocks and family heirlooms",
      ],
      cta: "Learn more about item buyback",
      howItWorksTitle: "How does it work?",
      steps: [
        { n: "1", title: "Visit or photos", text: "We assess the property on site or from your photos." },
        { n: "2", title: "Transparent estimate", text: "Valuable items are identified and appraised." },
        { n: "3", title: "Deducted quote", text: "Their value is deducted directly from the clearance price." },
      ],
    },
    services: {
      title: "Our house clearance services",
      text: "A complete service, tailored to every situation, across the entire Brussels Region.",
      cards: [
        { title: "House clearance", text: "Full clearance of a house, from attic to cellar.", link: "/vide-maison" },
        { title: "Apartment clearance", text: "Studios, apartments and buildings, on any floor.", link: "/debarras-appartement" },
        { title: "Cellar, attic, garage", text: "Ancillary spaces cluttered for years.", link: "/debarras-cave-grenier-garage" },
        { title: "Item buyback", text: "Appraisal and buyback of valuable items found on site.", link: "/rachat-objets" },
      ],
      learnMore: "Learn more",
    },
    process: {
      title: "How the intervention unfolds",
      text: "A simple process, from first contact to complete removal.",
      steps: [
        { n: "1", title: "Contact", text: "You call us or fill in the form." },
        { n: "2", title: "Free quote", text: "Fast estimate, on site or from photos." },
        { n: "3", title: "Intervention", text: "Full clearance on the agreed date." },
        { n: "4", title: "Property cleared", text: "The property is clean and ready to be handed over." },
      ],
    },
    communes: {
      title: "House clearance in the 19 communes of Brussels",
      text: "We operate across the entire Brussels-Capital Region, with good knowledge of every neighbourhood.",
      seeAll: "See all 19 communes we serve",
    },
    reassurance: [
      { title: "Professional service", text: "An experienced team, discreet and respectful of your home." },
      { title: "Fast intervention", text: "Time slots available quickly, including urgent cases." },
      { title: "Satisfied customers", text: "Interventions carried out with care, praised by our customers." },
    ],
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-white text-ink">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: "linear-gradient(120deg, #EAF2FC 0%, #F7FBFF 45%, #FFFFFF 68%)",
          }}
        />

        <div className="relative mx-auto flex max-w-[1500px] flex-col lg:grid lg:grid-cols-[2fr_3fr] lg:items-stretch">
          <div className="relative z-10 order-2 flex flex-col justify-center px-6 pb-14 pt-8 sm:py-16 lg:order-1 lg:px-10 lg:py-24 xl:px-14">
            <div className="inline-flex w-fit items-center gap-2 rounded-lg border border-accent/20 bg-accent/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-accent">
              <MapPinIcon className="h-4 w-4" />
              {c.heroBadge}
            </div>

            <h1 className="mt-6 max-w-xl font-serif text-4xl font-black leading-[1.1] tracking-tight md:text-5xl xl:text-[3.4rem]">
              {c.h1.before}
              <span className="text-accent">{c.h1.accent}</span>
              {c.h1.after}
            </h1>

            <div className="mt-5 h-1 w-16 rounded-full bg-accent" />

            <p className="mt-6 max-w-lg text-lg leading-relaxed text-ink/70">
              {c.heroText}
            </p>

            <div className="mt-9 flex flex-wrap gap-4">
              <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
                {PHONE_DISPLAY}
              </CtaLink>

              <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
                {c.freeQuote}
              </CtaLink>
            </div>
          </div>

          <div className="relative order-1 h-[300px] px-4 pt-6 sm:h-[420px] sm:px-6 lg:order-2 lg:h-[560px] lg:self-center lg:px-0 lg:pt-0">
            <div className="hero-photo-clip relative h-full w-full overflow-hidden rounded-3xl">
              <Image
                src="/images/hero/vide-maison-bruxelles-hero.webp"
                alt={c.heroImageAlt}
                fill
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="object-cover"
                priority
                fetchPriority="high"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/15 via-transparent to-transparent" />

              {/* Bandeau de réassurance — desktop/tablette : conservé tel
                  quel (docs/OPTIMISATION_HERO_MOBILE_VIDE_MAISON_BRUXELLES.md
                  §"Ne pas modifier... le design desktop actuel"). */}
              <div className="absolute bottom-5 left-5 right-5 hidden items-center gap-4 rounded-3xl bg-ink/55 px-6 py-5 text-white shadow-premium backdrop-blur sm:flex sm:right-auto sm:max-w-[420px] lg:bottom-8 lg:left-10">
                <ShieldCheckIcon className="h-12 w-12 shrink-0 text-cta-300" />
                <p className="text-lg font-bold leading-snug">
                  {c.heroCaptionBefore}
                  <span className="text-cta-300">{c.heroCaptionAccent}</span>
                </p>
              </div>

              {/* Badge compact — mobile uniquement : le grand bandeau
                  masquait le camion/l'Atomium sous ~640px. */}
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-center gap-2 rounded-2xl bg-ink/85 px-3 py-2.5 text-white shadow-[0_8px_30px_rgba(0,0,0,0.18)] backdrop-blur-md sm:hidden">
                <ShieldCheckIcon aria-hidden="true" className="h-6 w-6 shrink-0 text-cta-300" />
                <p className="whitespace-nowrap text-[13px] font-semibold leading-tight">
                  {c.heroCaptionMobileBefore}
                  <strong className="font-bold text-cta-300">{c.heroCaptionMobileAccent}</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BANDE FONCTIONNALITÉS */}
      <section className="bg-brand-50 py-14">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {c.features.map((feature, i) => {
              const Icon = FEATURE_ICONS[i];
              return (
                <div key={feature.title} className="flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-cta-100 text-cta-700">
                    <Icon className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="font-bold text-ink">{feature.title}</p>
                    <p className="mt-1 text-sm text-neutral-600">{feature.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* RACHAT D'OBJETS */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-sage/30 bg-sage/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-sage">
                <GiftIcon className="h-4 w-4" />
                {c.rachat.badge}
              </div>

              <h2 className="mt-4 font-serif text-3xl font-extrabold text-brand-900 md:text-4xl">
                {c.rachat.title}
              </h2>

              <p className="mt-4 text-neutral-700 leading-relaxed">
                {c.rachat.text}
              </p>

              <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {c.rachat.items.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm font-semibold text-ink">
                    <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-sage" />
                    {item}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap gap-4">
                <CtaLink href="/rachat-objets" variant="primary" icon={GiftIcon}>
                  {c.rachat.cta}
                </CtaLink>
              </div>
            </div>

            <div className="rounded-[2rem] border border-sand-line bg-white p-8 shadow-[0_20px_60px_rgba(28,37,33,0.08)]">
              <p className="font-serif text-lg font-bold text-ink">{c.rachat.howItWorksTitle}</p>
              <ol className="mt-5 space-y-5">
                {c.rachat.steps.map((step) => (
                  <li key={step.n} className="flex gap-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink font-serif font-bold text-white">
                      {step.n}
                    </span>
                    <div>
                      <p className="font-bold text-ink">{step.title}</p>
                      <p className="mt-1 text-sm text-neutral-600">{step.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-serif text-3xl font-extrabold text-brand-900 md:text-4xl">{c.services.title}</h2>
            <p className="mt-4 text-neutral-600">{c.services.text}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {c.services.cards.map((service, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <Link
                  key={service.title}
                  href={service.link}
                  className="group rounded-3xl border border-sand-line bg-sand p-6 transition hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(28,37,33,0.10)]"
                >
                  <Icon className="h-9 w-9 text-accent" />
                  <p className="mt-4 font-serif text-lg font-bold text-ink">{service.title}</p>
                  <p className="mt-2 text-sm text-neutral-600">{service.text}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-accent">
                    {c.services.learnMore}
                    <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-serif text-3xl font-extrabold text-brand-900 md:text-4xl">{c.process.title}</h2>
            <p className="mt-4 text-neutral-600">{c.process.text}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-4">
            {c.process.steps.map((step) => (
              <div key={step.n} className="rounded-3xl border border-sand-line bg-white p-6 shadow-[0_10px_30px_rgba(20,32,26,0.06)]">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent font-serif font-bold text-white">
                  {step.n}
                </span>
                <p className="mt-4 font-bold text-ink">{step.title}</p>
                <p className="mt-2 text-sm text-neutral-600">{step.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.freeQuote}
            </CtaLink>
          </div>
        </div>
      </section>

      {/* COMMUNES */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-serif text-3xl font-extrabold text-brand-900 md:text-4xl">{c.communes.title}</h2>
            <p className="mt-4 text-neutral-600">{c.communes.text}</p>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {FEATURED_COMMUNES.map((slug) => {
              const commune = bruxellesData[slug];
              if (!commune) return null;
              const displayName = commune.content[locale]?.displayName ?? commune.displayName;
              return (
                <Link
                  key={slug}
                  href={`/vide-maison/${slug}`}
                  className="group flex items-center justify-between rounded-2xl border border-sand-line bg-sand px-4 py-3.5 transition hover:border-accent hover:bg-white"
                >
                  <span className="text-sm font-bold text-ink">{displayName}</span>
                  <ArrowRightIcon className="h-4 w-4 text-accent transition-transform group-hover:translate-x-1" />
                </Link>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <Link href="/vide-maison" className="inline-flex items-center gap-2 font-bold text-accent hover:text-accent-dark">
              {c.communes.seeAll}
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* REASSURANCE */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            {c.reassurance.map((item, i) => {
              const Icon = REASSURANCE_ICONS[i];
              return (
                <div key={item.title} className="rounded-3xl border border-sand-line bg-white p-7 text-center">
                  <Icon className="mx-auto h-9 w-9 text-accent" />
                  <p className="mt-4 font-serif text-lg font-bold text-ink">{item.title}</p>
                  <p className="mt-2 text-sm text-neutral-600">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Contact />
    </div>
  );
}
